import classNames from 'classnames';
import React from 'react';
import { useParams } from 'react-router-dom';

import Button from 'components/button';
import Icon from 'icons';
import { useClientContext } from 'layouts/application-group';
import request from 'request';
import prettierStatus from 'utils/prettier-status';
import Input from '../input';
import styles from './result-box.module.scss';
import isDateNotInFuture from 'utils/is-data-is-not-in-future';

const ResultBox = () => {
	const { legalCaseList, group, OCRFileList, setGroup, pendingOCR, setPendingOCR } =
		useClientContext();

	const { clientId } = useParams();

	const [pendingLegalCase, setPendingLegalCase] = React.useState(false);
	const [pendingScoring, setPendingScoring] = React.useState(false);
	const [pendingSelectRecommendation, setPendingSelectRecommendation] = React.useState(false);
	const [decision, setDecision] = React.useState('');
	const [commentary, setCommentary] = React.useState('');

	const earlyRenewalEligibilityStyles = {
		grey:
			(group?.early_renewal_eligibility as string) === 'N/A' || !group?.early_renewal_eligibility,
		green: group?.early_renewal_eligibility === 'approved',
		yellow: group?.early_renewal_eligibility === 'review',
		red: group?.early_renewal_eligibility === 'rejected',
	};

	const OCRButtonIsBlocked = React.useMemo(
		() => !!OCRFileList.find((file) => !file.file),
		[OCRFileList],
	);

	const LegalCaseButtonIsBlocked = React.useMemo(
		() => !!legalCaseList.find((l) => l.hasLegalCase && l.legal_case_has_correct_type === null),
		[legalCaseList],
	);

	const ScoringButtonIsBlocked = React.useMemo(() => {
		const check = group.applications.every((application) => {
			return (
				application.scoring_log &&
				application.scoring_log.age !== null &&
				application.scoring_log.family_status !== null &&
				application.scoring_log.dependants !== null &&
				application.scoring_log.nationality !== null &&
				application.scoring_log.address !== null &&
				application.scoring_log.address.trim() !== '' &&
				application.repeated_client !== null &&
				application.prior_loans !== null &&
				(application.repeated_client === 'new' ||
					application.scoring_log.postpone_payments !== null) &&
				application.scoring_log.dual_loans !== null &&
				application.scoring_log.max_days_late_payments !== null &&
				application.scoring_log.client_status_gurantor !== null &&
				application.scoring_log.active_loan_with_same_family_id !== null &&
				application.scoring_log.family_members_delinquent !== null &&
				application.scoring_log.project_net_monthly_income !== null &&
				application.scoring_log.additional_income !== null &&
				application.scoring_log.monthly_payment_for_active_loans !== null &&
				application.scoring_log.client_has_early_settlement_loan !== null &&
				(application.scoring_log.client_has_early_settlement_loan === 'No' ||
					application.scoring_log.date_of_early_settlements_loan !== null) &&
				(application.scoring_log.client_has_early_settlement_loan === 'No' ||
					!isDateNotInFuture(
						application.scoring_log.date_of_early_settlements_loan.split('-').reverse().join('/'),
					)) &&
				application.scoring_log.number_of_active_upcoming_payments !== null &&
				application.scoring_log.residence_type !== null &&
				application.scoring_log.transport !== null &&
				application.scoring_log.transport_manufacturing_year !== null &&
				application.scoring_log.employment_status !== null &&
				application.scoring_log.employment_level !== null &&
				application.scoring_log.work_experience !== null &&
				application.scoring_log.phone_os !== null
			);
		});

		return !check;
	}, [group.applications]);

	const handleCalculateLegalCase = async () => {
		if (pendingLegalCase) return;
		setPendingLegalCase(true);
		try {
			await request.post(`/applications/group/${group.id}/legal_case`, {
				applications: legalCaseList.map((legalcase) => ({
					application_id: legalcase.id,
					legal_case_has_correct_type: legalcase.legal_case_has_correct_type,
				})),
			});
			await request
				.get(`/applications/group/${group.id}`)
				.then((res) => res.data)
				.then((res) => setGroup(res));
		} finally {
			setPendingLegalCase(false);
		}
	};

	const handleCalculateOCR = async () => {
		if (pendingOCR) return;
		setPendingOCR(true);
		try {
			const fd = new FormData();
			OCRFileList.forEach((OCRFile) => {
				fd.append('files', OCRFile.file!);
			});
			fd.append('application_ids', OCRFileList.map((OCRFile) => String(OCRFile.id)).join(','));
			await request({
				url: `/applications/group/${group.id}/ocr`,
				method: 'POST',
				data: fd,
				headers: { 'Content-Type': 'multipart/form-data' },
			});
			await request
				.get(`/applications/group/${group.id}`)
				.then((res) => res.data)
				.then((res) => setGroup(res));
		} finally {
			setPendingOCR(false);
		}
	};

	const handleCalculateScoring = async () => {
		if (pendingScoring) return;
		setPendingScoring(true);
		request
			.post(`/applications/group/${group.id}/scoring`, {
				applications: group.applications.map((a) => ({
					application_id: a.id,
					data: {
						scoring_data: {
							dependants: a.scoring_log?.dependants,
							family_status: a.scoring_log?.family_status,
							gender: a.scoring_log?.gender,
							city: a.scoring_log?.city,
							age: a.scoring_log?.age,
							prior_loans: a?.prior_loans,
						},
						collected_data: {
							branch: a.scoring_log?.branch,
							phone_os: a.scoring_log?.phone_os,
							transport: a.scoring_log?.transport,
							residence_type: a.scoring_log?.residence_type,
							employment_level: a.scoring_log?.employment_level,
							employment_status: a.scoring_log?.employment_status,
							work_experience: a.scoring_log?.work_experience,
							client_has_early_settlement_loan: a.scoring_log?.client_has_early_settlement_loan,
							transport_manufacturing_year: a.scoring_log?.transport_manufacturing_year,
							nationality: a.scoring_log?.nationality,
							address: a.scoring_log?.address,
							postpone_payments: a.scoring_log?.postpone_payments,
							project_net_monthly_income: a.scoring_log?.project_net_monthly_income,
							additional_income: a.scoring_log?.additional_income,
							monthly_payment_for_active_loans: a.scoring_log?.monthly_payment_for_active_loans,
							guarantor_income: a.scoring_log?.guarantor_income,
							guarantor_additional_income: a.scoring_log?.guarantor_additional_income,
							guarantor_total_monthly_payments: a.scoring_log?.guarantor_total_monthly_payments,
						},
						stop_factors: {
							number_of_active_upcoming_payments: a.scoring_log?.number_of_active_upcoming_payments,
							longest_payment_delay_by_guarantor: a.scoring_log?.longest_payment_delay_by_guarantor,
							date_of_early_settlements_loan: a.scoring_log?.date_of_early_settlements_loan,
							group_loan: a.scoring_log?.group_loan,
							loan_purpose: a.scoring_log?.loan_purpose,
							dual_loans: a.scoring_log?.dual_loans,
							max_days_late_payments: a.scoring_log?.max_days_late_payments,
							client_status_gurantor: a.scoring_log?.client_status_gurantor,
							family_members_loans: a.scoring_log?.family_members_loans,
							active_loan_with_same_family_id: a.scoring_log?.active_loan_with_same_family_id,
							current_application_with_same_family_id:
								a.scoring_log?.current_application_with_same_family_id,
							family_members_delinquent: a.scoring_log?.family_members_delinquent,
							guarantor_age: a.scoring_log?.guarantor_age,
							guarantor_has_active_loan: a.scoring_log?.guarantor_has_active_loan,
							guarantor_for_another_loan: a.scoring_log?.guarantor_for_another_loan,
						},
					},
				})),
			})
			.then(() => {
				request
					.get(`/applications/group/${group.id}`)
					.then((res) => res.data)
					.then((res) => setGroup(res));
			})
			.finally(() => {
				setPendingScoring(false);
			});
	};

	const handleFinalDecision = () => {
		setPendingSelectRecommendation(true);
		request
			.post(`/applications/group/${group?.id}/review`, {
				decision: decision === 'Approve' ? 'approved' : 'rejected',
				commentary: commentary,
			})
			.then(() =>
				request
					.get(`/applications/group/${group.id}`)
					.then((res) => res.data)
					.then((res) => setGroup(res)),
			)
			.finally(() => setPendingSelectRecommendation(false));
	};
	return (
		<div className={styles.result_list}>
			{!clientId && group?.expert_decision !== null && (
				<div className={classNames(styles.result_box, styles.final_decision)}>
					<div className={styles.title}>Final decision</div>
					<div className={styles.finalResult}>
						<Icon
							size={120}
							name={group?.expert_decision === 'approved' ? 'tickCircle' : 'closeFilled'}
							className={classNames(
								group?.expert_decision === 'approved' ? styles.green : styles.red,
							)}
						/>
						<div
							className={classNames(
								styles.finalResultText,
								group?.expert_decision === 'approved' ? styles.green : styles.red,
							)}
						>
							{prettierStatus(group?.expert_decision)}
						</div>
						<div className={styles.finalResultDescription}>
							{group?.expert_decision_description}
						</div>
					</div>
				</div>
			)}
			<div className={styles.result_box}>
				<div className={styles.title}>Group status (ID {group.id})</div>
				{group.is_approved && (
					<div className={styles.finalResult}>
						<Icon
							size={120}
							name={group.is_approved === 'approved' ? 'tickCircle' : 'closeFilled'}
							className={classNames(styles.finalResultText, {
								[styles.green]: group.is_approved === 'approved',
								[styles.red]: group.is_approved === 'rejected',
								[styles.yellow]:
									group.is_approved !== 'approved' && group.is_approved !== 'rejected',
							})}
						/>
						<div
							className={classNames(styles.finalResultText, {
								[styles.green]: group.is_approved === 'approved',
								[styles.red]: group.is_approved === 'rejected',
								[styles.yellow]:
									group.is_approved !== 'approved' && group.is_approved !== 'rejected',
							})}
						>
							{prettierStatus(group.is_approved)}
						</div>
					</div>
				)}
				<div className={styles.item}>
					<div className={styles.key}>OCR</div>
					{group.ocr_approved ? (
						<div
							className={classNames(styles.value, {
								[styles.red]: group.ocr_approved === 'rejected',
								[styles.yellow]: group.ocr_approved === 'review',
								[styles.green]:
									group.ocr_approved !== 'rejected' && group.ocr_approved !== 'review',
							})}
						>
							{prettierStatus(group.ocr_approved)}
						</div>
					) : (
						<Button
							blocked={OCRButtonIsBlocked}
							color={OCRButtonIsBlocked ? 'grey' : 'blue'}
							pending={pendingOCR}
							onClick={handleCalculateOCR}
						>
							Calculate
						</Button>
					)}
				</div>
				<div className={styles.item}>
					<div className={styles.key}>Manual stop factors</div>
					<div
						className={classNames(styles.value, {
							[styles.red]: group.stop_factors_approved === 'rejected',
							[styles.yellow]: group.stop_factors_approved === 'review',
							[styles.green]:
								group.stop_factors_approved !== 'rejected' &&
								group.stop_factors_approved !== 'review',
						})}
					>
						{prettierStatus(group.stop_factors_approved)}
					</div>
				</div>
				<div className={styles.item}>
					<div className={styles.key}>Scoring</div>
					{group.stop_factors_approved === 'rejected' ? (
						<div className={styles.value}>N/A</div>
					) : group.scoring_approved ? (
						<div
							className={classNames(styles.value, {
								[styles.red]: group.scoring_approved === 'rejected',
								[styles.yellow]: group.scoring_approved === 'review',
								[styles.green]:
									group.scoring_approved !== 'rejected' && group.scoring_approved !== 'review',
							})}
						>
							{prettierStatus(group.scoring_approved)} ({group.prediction?.toFixed(2)}%)
						</div>
					) : (
						<Button
							blocked={ScoringButtonIsBlocked}
							color={ScoringButtonIsBlocked ? 'grey' : 'blue'}
							pending={pendingScoring}
							onClick={handleCalculateScoring}
						>
							Calculate
						</Button>
					)}
				</div>
				<div className={styles.item}>
					<div className={styles.key}>Legal case</div>
					{group.legal_case_approved ? (
						<div
							className={classNames(styles.value, {
								[styles.red]: group.legal_case_approved === 'rejected',
								[styles.yellow]: group.legal_case_approved === 'review',
								[styles.green]:
									group.legal_case_approved !== 'rejected' &&
									group.legal_case_approved !== 'review',
							})}
						>
							{prettierStatus(group.legal_case_approved)}
						</div>
					) : (
						<Button
							blocked={LegalCaseButtonIsBlocked}
							color={LegalCaseButtonIsBlocked ? 'grey' : 'blue'}
							pending={pendingLegalCase}
							onClick={handleCalculateLegalCase}
						>
							Calculate
						</Button>
					)}
				</div>

				<div className={styles.item}>
					<div className={styles.key}>Early Renewal Eligibility:</div>
					<div
						className={classNames(styles.value, {
							[styles.grey]: earlyRenewalEligibilityStyles.grey,
							[styles.green]: earlyRenewalEligibilityStyles.green,
							[styles.yellow]: earlyRenewalEligibilityStyles.yellow,
							[styles.red]: earlyRenewalEligibilityStyles.red,
						})}
					>
						{group?.early_renewal_eligibility
							? prettierStatus(group?.early_renewal_eligibility) +
								(group?.early_renewal_eligibility_percentage_group
									? ` (${
											Number.isInteger(group?.early_renewal_eligibility_percentage_group * 100)
												? group?.early_renewal_eligibility_percentage_group * 100
												: (group?.early_renewal_eligibility_percentage_group * 100).toFixed(1)
										}%)`
									: '')
							: 'No decision'}
					</div>
				</div>
			</div>
			{!clientId &&
				group.ocr_approved &&
				group.scoring_approved &&
				group.legal_case_approved &&
				group?.is_approved === 'review' &&
				group?.expert_decision === null && (
					<div className={classNames(styles.result_box, styles.final_decision)}>
						<div className={styles.title}>Final decision</div>
						<Input
							value={decision}
							id="decision"
							title="Decision"
							placeholder="Select final decision"
							type="select"
							onSelect={(option) => setDecision(option)}
							options={['Approve', 'Reject']}
						/>
						<Input
							id="commentary"
							title="Comment"
							value={commentary}
							type="textarea"
							onInput={(value) => setCommentary(value)}
						/>
						{decision.trim() !== '' && commentary.trim() !== '' && (
							<Button
								color="violet"
								pending={pendingSelectRecommendation}
								onClick={handleFinalDecision}
							>
								Submit
							</Button>
						)}
					</div>
				)}
		</div>
	);
};

export default ResultBox;
